import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  HostListener,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, of } from 'rxjs';
import {
  takeUntil,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  catchError,
  finalize,
} from 'rxjs/operators';
import { CustomInputComponent, ValidationRule } from '../../../custom-input/custom-input.component';
import { CustomerService, CustomerData } from '../../../../services/customer.service';
import { NotificationService } from '../../../../services/notification.service'; // NotificationService;
import { RoleService } from '../../../../services/role.service';
import { LoggerService } from '../../../../services/logger.service';
import {
  CUSTOMER_EMAIL_VALIDATION_RULES,
  CUSTOMER_FIRST_NAME_VALIDATION_RULES,
  CUSTOMER_LAST_NAME_VALIDATION_RULES,
  CUSTOMER_PHONE_VALIDATION_RULES,
  validateCustomerEmail,
  validateCustomerPhone,
} from '../../../../shared/validators/customer-contact-field.rules';
import { CustomerFormData } from '../../services/add-order-state.service';

export interface LocationOption {
  id: string;
  name: string;
}

export interface CustomerResolvedEvent {
  customerId: string;
  customerForm: CustomerFormData;
  locationId: string;
  locationName: string;
  existingCustomer: any;
}

type CustomerSearchField = 'phone' | 'firstName' | 'email';

/**
 * Step 2 — Customer Information.
 * Handles customer search with debouncing, form validation,
 * location selection (admin-only), and emits resolved customer data.
 *
 * Reuses: CustomInputComponent.
 */
@Component({
  selector: 'app-customer-info-step',
  standalone: true,
  imports: [CommonModule, CustomInputComponent],
  templateUrl: './customer-info-step.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./customer-info-step.component.scss'],
})
export class CustomerInfoStepComponent implements OnInit, OnDestroy, OnChanges {
  @Input() locationOptions: LocationOption[] = [];
  @Input() isLoadingLocations = false;
  @Input() sectionTitle = 'Customer Information';
  /** Pre-fill form with data from state service (so going Back and Forward preserves data). */
  @Input() set initialForm(value: CustomerFormData) {
    this.customerForm = { ...value };
    this.syncLocationSelectFromForm();
  }
  @Input() set initialCustomerId(value: string) {
    this.customerId = value;
  }
  @Input() set initialExistingCustomer(value: any) {
    this.selectedExistingCustomer = value;
  }

  @Output() customerResolved = new EventEmitter<CustomerResolvedEvent>();
  @Output() back = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  @ViewChildren('customerSearchContainer') searchContainers?: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChild('locationDropdownRoot') locationDropdownRoot?: ElementRef<HTMLDivElement>;

  private readonly destroy$ = new Subject<void>();
  private readonly searchTerm$ = new Subject<{ term: string; field: CustomerSearchField }>();
  private readonly PHONE_MIN = 8;
  /** Match list API / notification-modal name search threshold */
  private readonly NAME_MIN = 2;
  private readonly EMAIL_MIN = 3;

  customerForm: CustomerFormData = {
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    location: '',
  };
  customerId = '';
  selectedExistingCustomer: any = null;

  isCheckingCustomer = false;
  isCreatingCustomer = false;
  customerValidationError = '';

  searchResults: any[] = [];
  isSearchLoading = false;
  showSearchDropdown = false;
  searchError = '';
  searchTriggered = false;
  activeSearchField: CustomerSearchField | null = null;

  /** Selected location id (synced with customerForm.location label). */
  selectedLocationId = '';
  /** Custom location dropdown open state (Figma-style control). */
  isLocationMenuOpen = false;
  /** Fixed viewport position so the list is not clipped by the modal footer / overflow. */
  locationPanelFixedStyle: Record<string, string> = {};

  // ── Validation rules ───────────────────────────────────────────────────────

  readonly firstNameValidation: ValidationRule[] = CUSTOMER_FIRST_NAME_VALIDATION_RULES;
  readonly lastNameValidation: ValidationRule[] = CUSTOMER_LAST_NAME_VALIDATION_RULES;
  readonly phoneValidation: ValidationRule[] = CUSTOMER_PHONE_VALIDATION_RULES;
  readonly emailValidation: ValidationRule[] = CUSTOMER_EMAIL_VALIDATION_RULES;

  constructor(
    private readonly customerService: CustomerService,
    public readonly roleService: RoleService,
    private readonly logger: LoggerService,
    private readonly messageService: NotificationService
  ) {}

  ngOnInit(): void {
    this.setupSearch();
    this.syncLocationSelectFromForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['locationOptions']) {
      this.syncLocationSelectFromForm();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Getters ────────────────────────────────────────────────────────────────

  /**
   * Controls Continue enabled state: only location is required (admins).
   * Staff skips location UI; name / phone / email stay optional.
   * Invalid optional values are checked in onContinue().
   */
  get isFormValid(): boolean {
    return this.bookingLocationOk;
  }

  private get bookingLocationOk(): boolean {
    return (
      this.roleService.isShopFloorEmployee() ||
      !!this.selectedLocationId ||
      !!(this.customerForm.location ?? '').trim()
    );
  }

  /** Name / phone / email optional; when provided, same rules as CustomInput. */
  private get bookingFieldsValid(): boolean {
    const firstName = (this.customerForm.firstName ?? '').trim();
    const lastName = (this.customerForm.lastName ?? '').trim();
    const phone = (this.customerForm.phone ?? '').trim();
    const email = (this.customerForm.email ?? '').trim();

    if (firstName.length > 50 || lastName.length > 50) return false;
    if (firstName && !/^[a-zA-Z\s'-]+$/.test(firstName)) return false;
    if (lastName && !/^[a-zA-Z\s'-]+$/.test(lastName)) return false;
    if (phone && validateCustomerPhone(phone)) return false;
    if (email && validateCustomerEmail(email)) return false;
    return true;
  }

  get isStaff(): boolean {
    return this.roleService.isShopFloorEmployee();
  }

  // ── Template helpers ───────────────────────────────────────────────────────

  trackLocationById(_: number, loc: LocationOption): string {
    return loc.id;
  }

  get locationTriggerLabel(): string {
    if (!this.selectedLocationId) return '';
    return this.locationOptions.find(l => l.id === this.selectedLocationId)?.name ?? '';
  }

  formatLastVisitDate(dateStr: string): string {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  }

  shouldShowDropdown(field: CustomerSearchField): boolean {
    return (
      this.showSearchDropdown &&
      this.activeSearchField === field &&
      (this.isSearchLoading || !!this.searchError || this.searchTriggered)
    );
  }

  // ── Customer search handlers ───────────────────────────────────────────────

  onFirstNameInput(value: string): void {
    this.searchTerm$.next({ term: value, field: 'firstName' });
  }

  onPhoneInput(value: string): void {
    this.searchTerm$.next({ term: value, field: 'phone' });
  }

  onEmailInput(value: string): void {
    this.searchTerm$.next({ term: value, field: 'email' });
  }

  onSelectExistingCustomer(customer: any): void {
    if (!customer) return;
    this.selectedExistingCustomer = customer;
    this.customerId = customer.id ?? customer.customer_id ?? customer.customerId ?? '';
    this.customerForm.firstName = customer.first_name ?? customer.firstName ?? '';
    this.customerForm.lastName = customer.last_name ?? customer.lastName ?? '';
    this.customerForm.phone = customer.phone ?? customer.contact ?? '';
    this.customerForm.email = customer.email ?? '';
    this.hideDropdown();
  }

  onOtherFieldFocus(): void {
    this.hideDropdown();
    this.closeLocationMenu();
  }

  /** When focus moves into Location UI: hide customer search only (do not close the location list — focusin on options/trigger would fire this and break selection). */
  onLocationFieldFocusIn(): void {
    this.hideDropdown();
  }

  /**
   * Closes the customer search panel when focus leaves the search field (tab/click elsewhere),
   * matching typical combobox behavior. Deferred so `document.activeElement` reflects the new target.
   * Clicks inside the dropdown use mousedown.preventDefault so the input keeps focus while interacting.
   */
  onSearchFieldBlur(field: CustomerSearchField): void {
    setTimeout(() => {
      if (!this.showSearchDropdown || this.activeSearchField !== field) {
        return;
      }
      const container = this.getSearchContainerForField(field);
      const active = document.activeElement;
      if (container && active && container.contains(active)) {
        return;
      }
      this.hideDropdown();
    }, 0);
  }

  private getSearchContainerForField(field: CustomerSearchField): HTMLElement | null {
    const containers = this.searchContainers?.toArray() ?? [];
    const idx = field === 'firstName' ? 0 : field === 'phone' ? 1 : 2;
    return containers[idx]?.nativeElement ?? null;
  }

  toggleLocationMenu(event: Event): void {
    event.stopPropagation();
    this.hideDropdown();
    if (this.isLoadingLocations || this.locationOptions.length === 0) return;
    this.isLocationMenuOpen = !this.isLocationMenuOpen;
    if (this.isLocationMenuOpen) {
      this.scheduleLocationPanelPosition();
    } else {
      this.locationPanelFixedStyle = {};
    }
  }

  selectLocationOption(loc: LocationOption, event: Event): void {
    event.stopPropagation();
    this.selectedLocationId = loc.id;
    this.customerForm.location = loc.name;
    this.isLocationMenuOpen = false;
  }

  closeLocationMenu(): void {
    this.isLocationMenuOpen = false;
    this.locationPanelFixedStyle = {};
  }

  /** Reposition when the booking step body scrolls (fixed panel must track the trigger). */
  onBookingContentScroll(): void {
    if (this.isLocationMenuOpen) {
      this.scheduleLocationPanelPosition();
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.isLocationMenuOpen) {
      this.scheduleLocationPanelPosition();
    }
  }

  private scheduleLocationPanelPosition(): void {
    requestAnimationFrame(() => this.updateLocationPanelFixedPosition());
  }

  /**
   * Uses position:fixed so options are not hidden behind the sticky footer.
   * Opens upward when there is more room above the trigger than below (viewport).
   */
  private updateLocationPanelFixedPosition(): void {
    if (!this.isLocationMenuOpen || !this.locationDropdownRoot?.nativeElement) {
      this.locationPanelFixedStyle = {};
      return;
    }
    const root = this.locationDropdownRoot.nativeElement;
    const trigger = root.querySelector('.location-dropdown-trigger') as HTMLElement | null;
    if (!trigger) {
      this.locationPanelFixedStyle = {};
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const maxH = 240;
    const gap = 6;
    const rowApprox = 44;
    const estH = Math.min(this.locationOptions.length * rowApprox + 16, maxH);
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const preferUp = spaceBelow < Math.min(estH, 140) && spaceAbove >= spaceBelow;

    let top: number;
    let maxHeight = maxH;
    if (preferUp) {
      const h = Math.min(estH, spaceAbove);
      top = rect.top - h - gap;
      if (top < 8) {
        maxHeight = Math.max(72, rect.top - gap - 8);
        top = 8;
      } else {
        maxHeight = Math.min(maxH, h);
      }
    } else {
      top = rect.bottom + gap;
      const avail = window.innerHeight - top - 8;
      maxHeight = Math.min(maxH, Math.max(72, avail));
    }

    this.locationPanelFixedStyle = {
      top: `${Math.round(top)}px`,
      left: `${Math.round(rect.left)}px`,
      width: `${Math.round(rect.width)}px`,
      maxHeight: `${Math.round(maxHeight)}px`,
    };
  }

  // ── Navigation ─────────────────────────────────────────────────────────────

  /**
   * Continues the order flow after validating customer and booking information.
   * @example
   * nContinue()
   * void
   * @returns {void} Does not return a value.
   **/
  onContinue(): void {
    if (!this.bookingLocationOk || this.isCheckingCustomer || this.isCreatingCustomer) {
      return;
    }

    if (!this.bookingFieldsValid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Check customer details',
        detail: 'Please fix invalid name, phone, or email before continuing.',
      });
      return;
    }

    if (this.customerId) {
      this.emitCustomerResolved();
      return;
    }

    this.createNewCustomerThenContinue();
  }

  private emitCustomerResolved(): void {
    this.customerResolved.emit({
      customerId: this.customerId,
      customerForm: { ...this.customerForm },
      locationId: this.getLocationId(),
      locationName: this.customerForm.location,
      existingCustomer: this.selectedExistingCustomer,
    });
  }

  /**
   * Creates a new regular customer from the form data and continues the flow when successful.
   * @example
   * createNewCustomerThenContinue()
   * undefined
   * @returns {void} Does not return a value.
   **/
  private createNewCustomerThenContinue(): void {
    this.isCreatingCustomer = true;
    // API expects type "regular", empty contact/email allowed; do not send location_id on create.
    const payload: CustomerData = {
      firstName: this.customerForm.firstName.trim(),
      lastName: this.customerForm.lastName.trim(),
      phone: this.customerForm.phone.trim(),
      email: this.customerForm.email.trim(),
      licensePlate: '',
      customerType: 'Regular',
    };

    this.customerService
      .createCustomer(payload, false)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isCreatingCustomer = false;
        })
      )
      .subscribe({
        next: (res: any) => {
          const id = res?.data?.customer_id ?? res?.data?.id ?? res?.data?.customer?.id ?? '';
          if (id) {
            this.customerId = String(id);
            this.selectedExistingCustomer = null;
            this.emitCustomerResolved();
            return;
          }
          const msg = res?.message || 'Could not create customer. Please try again.';
          this.messageService.add({ severity: 'error', summary: 'Customer', detail: msg });
        },
        error: err => {
          this.logger.trackError('CustomerInfoStep.createCustomer', err);
          const status = err?.status;
          if (status === 409 || err?.error?.status_code === 409) {
            this.tryCreateCustomerForce(payload);
            return;
          }
          const detail =
            err?.error?.message ?? err?.message ?? 'Could not create customer. Please try again.';
          this.messageService.add({ severity: 'error', summary: 'Customer', detail });
        },
      });
  }

  /**
   * Force-creates a customer record and resolves the created customer ID on success.
   * @example
   * tryCreateCustomerForce(payload)
   * void
   * @param {CustomerData} payload - Customer data used to create the customer.
   * @returns {void} This method does not return a value.
   */
  private tryCreateCustomerForce(payload: CustomerData): void {
    this.isCreatingCustomer = true;
    this.customerService
      .createCustomer(payload, true)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isCreatingCustomer = false;
        })
      )
      .subscribe({
        next: (res: any) => {
          const id = res?.data?.customer_id ?? res?.data?.id ?? res?.data?.customer?.id ?? '';
          if (id) {
            this.customerId = String(id);
            this.selectedExistingCustomer = null;
            this.emitCustomerResolved();
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Customer',
              detail: res?.message || 'Could not save customer.',
            });
          }
        },
        error: err => {
          this.logger.trackError('CustomerInfoStep.createCustomerForce', err);
          const detail =
            err?.error?.message ?? err?.message ?? 'Could not save customer. Please try again.';
          this.messageService.add({ severity: 'error', summary: 'Customer', detail });
        },
      });
  }

  // ── Document-level click listener ─────────────────────────────────────────

  /**
   * Handles document click events to close open dropdowns when the click occurs outside relevant containers.
   * @example
   * onDocumentClick(event)
   * undefined
   * @param {Event} event - The click event emitted from the document.
   * @returns {void} This method does not return a value.
   **/
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as Node;
    if (this.showSearchDropdown) {
      const containers = this.searchContainers?.toArray() ?? [];
      if (!containers.some(c => c.nativeElement.contains(target))) {
        this.hideDropdown();
      }
    }
    if (this.isLocationMenuOpen && this.locationDropdownRoot?.nativeElement) {
      if (!this.locationDropdownRoot.nativeElement.contains(target)) {
        this.isLocationMenuOpen = false;
      }
    }
  }

  // ── Private ────────────────────────────────────────────────────────────────

  /**
   * Sets up the customer search stream, applying input validation, debouncing, and API lookup handling.
   * @example
   * setupSearch()
   * void
   * @returns {void} No value is returned.
   **/
  private setupSearch(): void {
    this.searchTerm$
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(400),
        distinctUntilChanged((a, b) => a.term === b.term && a.field === b.field),
        switchMap(({ term, field }) => {
          const trimmed = (term ?? '').trim();
          const digits = trimmed.replace(/\D/g, '');
          const isPhone = field === 'phone';
          const isEmail = field === 'email';
          const meetsMin = isPhone
            ? digits.length >= this.PHONE_MIN || trimmed.length >= this.PHONE_MIN
            : isEmail
              ? trimmed.length >= this.EMAIL_MIN
              : trimmed.length >= this.NAME_MIN;

          if (!trimmed || !meetsMin) {
            this.resetSearchState(true);
            return of(null);
          }

          this.activeSearchField = field;
          this.isSearchLoading = true;
          this.searchError = '';
          this.searchResults = [];
          this.showSearchDropdown = true;
          this.searchTriggered = true;

          const params: Record<string, string> = isPhone
            ? { contact: trimmed }
            : isEmail
              ? { search: trimmed }
              : { name: trimmed };

          return this.customerService.getCustomers(params).pipe(
            catchError(err => {
              this.logger.trackError('CustomerInfoStep.search', err);
              this.searchError = 'Unable to search customers right now. Please try again.';
              this.isSearchLoading = false;
              return of({ error: true });
            })
          );
        })
      )
      .subscribe((res: any) => {
        this.isSearchLoading = false;
        if (!res || res.error) {
          this.searchResults = [];
          return;
        }
        this.searchResults = this.extractCustomers(res);
        this.searchError = '';
      });
  }

  private extractCustomers(res: any): any[] {
    if (Array.isArray(res)) return res;
    // Primary list API shape (see customer-table / notification-modal)
    if (Array.isArray(res?.data?.customer)) return res.data.customer;
    if (Array.isArray(res?.data?.customers)) return res.data.customers;
    if (Array.isArray(res?.data)) return res.data;
    return [];
  }

  private resetSearchState(keepInput = false): void {
    if (!keepInput) this.customerForm.phone = '';
    this.searchResults = [];
    this.showSearchDropdown = false;
    this.isSearchLoading = false;
    this.searchError = '';
    this.searchTriggered = false;
    this.activeSearchField = null;
  }

  private hideDropdown(): void {
    this.showSearchDropdown = false;
  }

  private getLocationId(): string {
    if (this.selectedLocationId) return this.selectedLocationId;
    const loc = this.locationOptions.find(l => l.name === this.customerForm.location);
    return loc?.id ?? '';
  }

  private syncLocationSelectFromForm(): void {
    const label = (this.customerForm.location ?? '').trim();
    if (!label) {
      this.selectedLocationId = '';
      return;
    }
    const byName = this.locationOptions.find(l => l.name === label);
    const byId = this.locationOptions.find(l => l.id === label);
    this.selectedLocationId = byName?.id ?? byId?.id ?? '';
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
